import {addToast} from '@albert-io/atomic';
import notifier from '@albert-io/notifier';
import {debounce} from 'lodash';
import React, {useCallback, useState} from 'react';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';
import {immer} from 'zustand/middleware/immer';

import constants from 'client/constants';
import {history} from 'client/history';
import {redirectToLoginPageFromLocation} from 'client/LogIn/utils';

import {normalizeError} from 'lib/normalizeError';
import {useResetPasswordWithTokenMutation} from './ResetPassword.queries.generated';

/**
 * Error messages returned by the resetPasswordWithToken GQL mutation.
 * The backend returns these as plain strings in the `error` field of ResetPasswordResult.
 */
const RESET_PASSWORD_ERRORS = {
  INVALID_LINK: 'Password reset link is invalid.',
  WEAK_PASSWORD:
    'New password must be at least 8 characters and contain a special character or number.',
  UPDATE_FAILED: 'Failed to update password.',
  RATE_LIMITED: 'Rate limit exceeded. Please try again later.'
} as const;

export type ResetPasswordState = {
  passwordInputValue: string;
  isPasswordValid: boolean;
  emailLinkId: string;
  userId: string;
};

export type ResetPasswordActions = {
  setParameters: (params: {userId: string; emailLinkId: string}) => void;
  handlePasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  reset: () => void;
};

const initialState: ResetPasswordState = {
  passwordInputValue: '',
  isPasswordValid: true,
  emailLinkId: '',
  userId: ''
};

// Validates password using the existing regex pattern
const validatePassword = (password: string): boolean => {
  if (!password) return true; // Empty password is considered valid (no error shown)
  return constants.REGEX_COLLECTION.PASSWORD.test(password);
};

export const useResetPasswordStore = create<ResetPasswordState & ResetPasswordActions>()(
  devtools(
    immer((set) => {
      // Debounced password validation
      const debouncedValidatePassword = debounce((password: string) => {
        set(
          (state) => {
            state.isPasswordValid = validatePassword(password);
          },
          false,
          'validatePassword'
        );
      }, 500);

      return {
        ...initialState,

        setParameters: ({userId, emailLinkId}) => {
          set(
            (state) => {
              state.userId = userId;
              state.emailLinkId = emailLinkId;
            },
            false,
            'setParameters'
          );
        },

        handlePasswordChange: (e) => {
          const value = e.target.value;
          set(
            (state) => {
              state.passwordInputValue = value;
            },
            false,
            'handlePasswordChange'
          );
          debouncedValidatePassword(value);
        },

        reset: () => {
          debouncedValidatePassword.cancel();
          set(initialState, false, 'reset');
        }
      };
    }),
    {name: 'ResetPasswordStore'}
  )
);

/**
 * Hook that provides the password reset submission logic.
 * Uses the GraphQL resetPasswordWithToken mutation.
 * Returns the submit function and the isSubmitting state.
 */
export const useResetPasswordSubmit = () => {
  const [resetPasswordMutation] = useResetPasswordWithTokenMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitNewPassword = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const state = useResetPasswordStore.getState();
      const {passwordInputValue, isPasswordValid, userId, emailLinkId} = state;

      // Validate before submission
      if (!passwordInputValue || !isPasswordValid) {
        return;
      }

      setIsSubmitting(true);

      try {
        const {data} = await resetPasswordMutation({
          variables: {
            userId,
            input: {
              passwordResetLink: emailLinkId,
              newPassword: passwordInputValue
            }
          }
        });

        if (data?.resetPasswordWithToken.error) {
          const errorMessage = data.resetPasswordWithToken.error;

          if (errorMessage === RESET_PASSWORD_ERRORS.INVALID_LINK) {
            addToast({
              color: 'negative',
              title: 'Unable to reset password.',
              message:
                'The code you are using is invalid. Please request another reset password email.'
            });
            history.replaceState(null, '/forgot-password');
            return;
          }

          if (errorMessage === RESET_PASSWORD_ERRORS.RATE_LIMITED) {
            addToast({
              color: 'negative',
              title: 'Unable to reset password.',
              message: 'Too many attempts. Please wait a moment and try again.'
            });
            return;
          }

          // Log unexpected error messages for debugging
          notifier.notify(new Error(`Unexpected reset password error: ${errorMessage}`), {
            tags: 'ResetPassword',
            context: {
              userId,
              errorMessage
            }
          });

          addToast({
            color: 'negative',
            title: 'Unable to reset password.',
            message: 'We were not able to complete your password change request. Please try again.'
          });
          return;
        }

        if (data?.resetPasswordWithToken.success) {
          addToast({
            color: 'positive',
            title: 'Password has been changed successfully!',
            message: 'Please Log In with your new password'
          });
          redirectToLoginPageFromLocation();
        }
      } catch (err: any) {
        const appError = normalizeError(err);

        notifier.notify(err, {
          message: 'Failed to reset password with unexpected error',
          tags: 'ResetPassword',
          context: {
            userId,
            errorKind: appError.kind,
            errorDetail: appError.detail
          }
        });

        addToast({
          color: 'negative',
          title: 'Unable to reset password.',
          message: 'We were not able to complete your password change request. Please try again.'
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [resetPasswordMutation]
  );

  return {submitNewPassword, isSubmitting};
};
